import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ref, uploadBytes } from 'firebase/storage';

import { useNavigate, useParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Slide from '@mui/material/Slide';
import Stack from '@mui/material/Stack';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';

import GratiPageWrapper, { WrapperAction } from '../components/common/GratiPageWrapper';
import TeamGeneralSettingsEditor from '../components/team/TeamGeneralSettingsEditor';
import TeamReviewAndFinalize from '../components/team/TeamReviewAndFinalize';
import { useApp } from '../contextProviders/AppProvider';
import { useData } from '../contextProviders/DataProvider';
import useLeague from '../dataHooks/useLeague';
import { useTeamMutators } from '../dataMutators/useTeamMutators';
import { storage } from '../firebase';
import DefaultObjects from '../types/DefaultObjects';
import Person from '../types/Person';
import Team from '../types/Team';
import { generateFirestoreId } from '../utils/utils';

type Step = 'edit' | 'review';

export default function TeamCreatePage(): ReactElement {
  const { orgId, leagueId } = useParams();
  const navigate = useNavigate();
  const { setPageName } = useApp();
  const { isLeagueAdmin, userState } = useData();
  const { addTeam } = useTeamMutators();

  const [step, setStep] = useState<Step>('edit');
  const [isEditing, setIsEditing] = useState(false);
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null);
  const [pageActions, setPageActions] = useState<WrapperAction | null>(null);

  // Pre-generate the document ID so we can use it for the logo path before saving
  const [docId] = useState(() => generateFirestoreId());

  const { league, isLeagueLoading, leagueError } = useLeague({ orgId, leagueId });

  const initialTeam = useMemo((): Team => {
    return {
      ...DefaultObjects.Team,
      itemId: docId,
      creatorId: userState.userProfile?.itemId || '',
      orgId: league?.orgId ?? orgId ?? '',
      orgName: league?.orgName ?? '',
      leagueId: league?.itemId ?? leagueId ?? '',
      leagueName: league?.name ?? '',
      name: '',
    };
  }, [docId, league, orgId, leagueId, userState.userProfile?.itemId]);

  const [currentTeam, setCurrentTeam] = useState<Team>(initialTeam);

  // Update team when league loads
  useEffect(() => {
    if (league) {
      setCurrentTeam((prev) => ({
        ...prev,
        orgId: league.orgId,
        orgName: league.orgName,
        leagueId: league.itemId,
        leagueName: league.name,
      }));
    }
  }, [league]);

  // Update creatorId if user profile loads later
  useEffect(() => {
    setCurrentTeam((prev) => ({
      ...prev,
      creatorId: initialTeam.creatorId,
    }));
  }, [initialTeam.creatorId]);

  useEffect(() => {
    const pageTitles: Record<Step, string> = {
      edit: 'Create team',
      review: 'Review and create',
    };
    setPageName(pageTitles[step]);
  }, [setPageName, step]);

  // Handle browser back button — navigate back to originating page (not just edit step)
  useEffect(() => {
    if (step !== 'review') return;

    window.history.pushState({ step: 'review' }, '');

    const handlePopState = () => {
      // The pushed review entry was already popped by the back button; go back one more to leave the create page
      navigate(-1);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [step, navigate]);

  const handleCancel = useCallback(() => {
    const stepsBack = step === 'review' ? -2 : -1;
    navigate(stepsBack);
  }, [navigate, step]);

  const handleReview = useCallback(() => {
    setStep('review');
  }, []);

  const handleCreate = useCallback(async () => {
    try {
      // Strip undefined values — Firestore rejects them in setDoc
      let teamToSave = Object.fromEntries(
        Object.entries(currentTeam).filter(([, v]) => v !== undefined),
      ) as Team;
      console.log("teamToSave: ", teamToSave);
      // If there's a pending logo file, upload it now
      if (pendingLogoFile) {
        const logoRef = ref(
          storage,
          `org/${teamToSave.orgId}/league/${teamToSave.leagueId}/team/${teamToSave.itemId}/orgMemberTeamManagerFile/${teamToSave.itemId}-logo`,
        );
        const snapshot = await uploadBytes(logoRef, pendingLogoFile);
        teamToSave = { ...teamToSave, logo: snapshot.ref.name };
      }

      const createdTeam = await addTeam(teamToSave);
      if (createdTeam) {
        // Go back 2 steps to clear both the review pushState and the create page entry,
        // then navigate to the league teams page
        navigate(-2);
        navigate(`/league-teams/${createdTeam.orgId}/${createdTeam.leagueId}`);
      }
    } catch (error) {
      console.error('Error saving team:', error);
    }
  }, [currentTeam, pendingLogoFile, addTeam, navigate]);

  const handleUpdate = useCallback(<K extends keyof Team>(field: K, value: Team[K]) => {
    setCurrentTeam((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleFileSelected = useCallback((file: File | null) => {
    setPendingLogoFile(file);
  }, []);

  const validateUser = (userProfile: Person | null | undefined) => {
    return userProfile && leagueId && isLeagueAdmin(leagueId, league?.orgId || '') ? true : false;
  };

  // Store callbacks in refs to avoid infinite loops in useEffect
  const handleCancelRef = useRef(handleCancel);
  const handleReviewRef = useRef(handleReview);
  const handleCreateRef = useRef(handleCreate);

  useEffect(() => {
    handleCancelRef.current = handleCancel;
    handleReviewRef.current = handleReview;
    handleCreateRef.current = handleCreate;
  });

  // Build page actions based on step
  useEffect(() => {
    let actions: ReactElement;
    let title: string;

    switch (step) {
      case 'edit':
        title = 'Create a new team';
        actions = (
          <Stack direction="row" spacing={2} sx={{
            alignItems: "center"
          }}>
            <Button
              size="small"
              variant="text"
              color="secondary"
              onClick={() => handleCancelRef.current()}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              endIcon={<ArrowForwardIcon />}
              onClick={() => handleReviewRef.current()}
              disabled={!currentTeam.name.trim()}
            >
              Review
            </Button>
          </Stack>
        );
        break;

      case 'review':
        title = 'Review and create';
        actions = (
          <Stack direction="row" spacing={2} sx={{
            alignItems: "center"
          }}>
            <Button
              size="small"
              variant="text"
              color="secondary"
              onClick={() => handleCancelRef.current()}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={<CheckIcon />}
              onClick={() => handleCreateRef.current()}
            >
              Create
            </Button>
          </Stack>
        );
        break;
    }

    setPageActions({
      title: { sm: title },
      actions,
    });
  }, [step, currentTeam.name]);

  return (
    <GratiPageWrapper
      validateUser={validateUser}
      isContentLoading={isLeagueLoading && !league}
      contentError={leagueError}
      pageActions={pageActions}
    >
      {step === 'edit' && (
        <Box sx={{ pt: 1 }}>
          <Stack spacing={2}>
            <TeamGeneralSettingsEditor
              team={currentTeam}
              isNew={true}
              onUpdate={handleUpdate}
              onFileSelected={handleFileSelected}
            />
          </Stack>
        </Box>
      )}
      {step === 'review' && (
        <Grid container spacing={3} sx={{ pt: 2, alignItems: 'stretch', overflow: 'hidden' }}>
          <Grid
            size={{ xs: 12, md: isEditing ? 5 : 12 }}
            sx={{ transition: 'all 300ms ease-in-out' }}
          >
            <TeamReviewAndFinalize
              team={currentTeam}
              isEditing={isEditing}
              onEditingOpen={() => setIsEditing(true)}
              pendingLogoFile={pendingLogoFile}
            />
          </Grid>
          <Grid
            size={{ xs: 12, md: isEditing ? 7 : 0 }}
            sx={{
              display: 'flex',
              transition: 'all 300ms ease-in-out',
              overflow: 'hidden',
            }}
          >
            <Slide direction="left" in={isEditing} mountOnEnter unmountOnExit>
              <Box
                sx={{
                  borderLeft: 2,
                  borderColor: 'divider',
                  pl: 2,
                  flexGrow: 1,
                }}
              >
                <TeamGeneralSettingsEditor
                  team={currentTeam}
                  isNew={true}
                  onUpdate={handleUpdate}
                  onFileSelected={handleFileSelected}
                />
              </Box>
            </Slide>
          </Grid>
        </Grid>
      )}
    </GratiPageWrapper>
  );
}
